import React, { useMemo } from 'react';

import { axisScale, curve, referenceMarks, scaleBounds, valueLabels, xAxisName, xLabelSpacing, xTitleRoom, yAxisName } from '../axisStyle';
import { formatAxis, formatValue, monthOf, unitName } from '../format';
import type { Palette } from '../palette';
import { overrideColor, seriesOverrides } from '../seriesStyle';
import type { Block, Envelope, EnvelopeColumn } from '../types';

import Chart, { type ChartOption } from './Chart';
import type { LegendItem } from './Legend';

type ComboProps = { block: Block; envelope: Envelope; palette: Palette };

export function comboOption({ block, envelope, palette }: ComboProps): ChartOption {
  const overrides = seriesOverrides(block);
  const measures = envelope.columns.filter((column) => column.role === 'measure') as Array<EnvelopeColumn>;
  const marks = (block.marks ?? {}) as Record<string, string>;
  const declared = (Array.isArray(block.right) ? block.right : []) as Array<string>;
  const lower = (measure: EnvelopeColumn, index: number) => declared.includes(measure.name) || (!declared.length && index > 0 && measure.format !== measures[0].format);
  const panels = measures.some(lower) ? 2 : 1;
  const days = [...new Set(envelope.rows.map((row) => String(row.t)))].sort();
  const byDay = new Map(envelope.rows.map((row) => [String(row.t), row]));
  const markOf = (measure: EnvelopeColumn, index: number) => marks[measure.name] ?? (index === 0 ? 'bar' : 'line');
  const isBar = (measure: EnvelopeColumn, index: number) => markOf(measure, index) === 'bar';
  const lines = measures.filter((measure, index) => !isBar(measure, index));
  const labelWidth = lines.length ? Math.max(...lines.map((measure) => measure.label.length)) * 6.4 + 14 : 12;
  const height = panels === 2 ? 44 : 84;
  const grids = Array.from({ length: panels }, (_, panel) => ({ left: 4, right: labelWidth, top: panel === 0 ? '12%' : '58%', height: `${height - 4 - (panel === panels - 1 ? xTitleRoom(block) / 4 : 0)}%`, containLabel: true }));
  const firstBelow = measures.findIndex((measure, index) => panels === 2 && lower(measure, index));
  const series = measures.flatMap((measure, index) => {
    if (overrides[measure.name]?.hidden) return [];
    const bar = isBar(measure, index);
    const color = overrideColor(overrides[measure.name], palette, palette.series[index % palette.series.length]);
    const panel = panels === 2 && lower(measure, index) ? 1 : 0;
    const label = overrides[measure.name]?.label ?? measure.label;
    return {
      id: measure.name,
      name: label,
      type: bar ? 'bar' : 'line',
      xAxisIndex: panel,
      yAxisIndex: panel,
      data: days.map((day) => (byDay.get(day)?.[measure.name] as number | null) ?? null),
      showSymbol: false,
      barMaxWidth: 18,
      itemStyle: { color, opacity: bar ? 0.85 : 1 },
      lineStyle: { width: 1.75, color },
      endLabel: { show: !bar, formatter: () => label, color, fontSize: 11, fontFamily: palette.sans, distance: 6 },
      tooltip: { valueFormatter: (value: number) => formatValue(value, measure.format) },
      ...(panel === 0 && index === 0 ? referenceMarks(block, palette, measure.format) : {}),
      ...(index === firstBelow ? referenceMarks(block, palette, measure.format, 'right') : {}),
      ...valueLabels(block, days.map((day) => (byDay.get(day)?.[measure.name] as number | null) ?? null), measure.format, palette),
      ...(bar ? {} : curve(block)),
      ...(markOf(measure, index) === 'area' ? { areaStyle: { color, opacity: 0.1 } } : {}),
    };
  });
  const formatOf = (panel: number) => measures.find((measure, index) => (panels === 2 && lower(measure, index) ? 1 : 0) === panel)?.format;
  return {
    animation: false,
    textStyle: { fontFamily: palette.sans, fontSize: 11, color: palette.ink3 },
    axisPointer: { link: [{ xAxisIndex: 'all' }] },
    grid: grids,
    tooltip: { trigger: 'axis', backgroundColor: palette.surface, borderColor: palette.hairlineStrong, textStyle: { color: palette.ink, fontSize: 12, fontFamily: palette.sans }, confine: true },
    xAxis: grids.map((_, panel) => ({
      type: 'category',
      gridIndex: panel,
      data: days,
      boundaryGap: true,
      axisLine: { lineStyle: { color: palette.hairlineStrong } },
      axisTick: { show: false },
      ...(panel === panels - 1 ? xAxisName(block, palette) : {}),
      axisLabel: { show: panel === panels - 1, color: palette.ink3, fontSize: 11, hideOverlap: true, interval: (index: number, day: string) => index === 0 || day.slice(5, 7) !== days[index - 1]?.slice(5, 7), formatter: (day: string) => monthOf(day), ...xLabelSpacing(block) },
    })),
    yAxis: grids.map((_, panel) => ({
      type: 'value',
      gridIndex: panel,
      triggerEvent: true,
      ...(panel === 0 ? scaleBounds(axisScale(block)) : scaleBounds(axisScale({ ...block, scale: block.scale2 }))),
      splitNumber: panels === 2 ? 2 : 4,
      ...(panel === 0 ? yAxisName(block, unitName(formatOf(panel), palette.ink3), palette) : unitName(formatOf(panel), palette.ink3)),
      axisLabel: { color: palette.ink3, fontSize: 11, formatter: (value: number) => formatAxis(value, formatOf(panel)) },
      splitLine: { lineStyle: { color: palette.hairline } },
    })),
    series,
  };
}

export function comboLegend(envelope: Envelope, block: Block, palette: Palette): Array<LegendItem> {
  const measures = envelope.columns.filter((column) => column.role === 'measure');
  const marks = (block.marks ?? {}) as Record<string, string>;
  const overrides = seriesOverrides(block);
  return measures.length < 2
    ? []
    : measures.flatMap((measure, index) =>
        (marks[measure.name] ?? (index === 0 ? 'bar' : 'line')) === 'bar' && !overrides[measure.name]?.hidden
          ? [{ name: overrides[measure.name]?.label ?? measure.label, color: overrideColor(overrides[measure.name], palette, palette.series[index % palette.series.length]), mark: 'square' as const, part: `series:${measure.name}` }]
          : [],
      );
}

export default function Combo({ block, envelope, palette }: ComboProps) {
  const option = useMemo(() => comboOption({ block, envelope, palette }), [block, envelope, palette]);
  return <Chart option={option} />;
}
